import { useCallback, useEffect, useRef, type RefObject } from 'react';

import { attemptFocus } from '../../../../core/utils/focus-management/attempt-focus.js';

/**
 * Restores focus to the action menu trigger when the menu is closed via the keyboard.
 *
 * The action menus unmount the whole `Menu` while closed (`Menu.Trigger` included). Therefore,
 * the trigger that the menu would restore focus to is already gone and focus falls back to
 * the body. Attach `triggerRef` to the trigger of the closed menu and pass `onCloseAutoFocus`
 * to `Menu.Content` to focus the surviving trigger instead.
 * @param isMenuOpen - whether the menu is currently open.
 * @internal
 */
export function useRestoreFocusOnMenuClose<T extends HTMLElement>(
  isMenuOpen: boolean,
): {
  triggerRef: RefObject<T | null>;
  onCloseAutoFocus: (event: Event) => void;
} {
  const triggerRef = useRef<T>(null);
  const restoreFocusOnCloseRef = useRef(false);

  // Capture phase, because the menu's own escape handler would otherwise close
  // the menu before the flag is recorded.
  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    restoreFocusOnCloseRef.current = false;

    // Escape, Enter, Space, and Tab all close the menu and should hand focus back.
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (
        event.key === 'Escape' ||
        event.key === 'Enter' ||
        event.key === 'Tab' ||
        event.key === ' '
      ) {
        restoreFocusOnCloseRef.current = true;
      }
    };

    // Any pointer interaction cancels a pending restore, e.g. Enter on a
    // sub-trigger followed by a click outside must not pull focus back.
    const handlePointerDown = (): void => {
      restoreFocusOnCloseRef.current = false;
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [isMenuOpen]);

  const onCloseAutoFocus = useCallback((event: Event): void => {
    if (!restoreFocusOnCloseRef.current) {
      return;
    }
    restoreFocusOnCloseRef.current = false;

    // `focusVisible` is needed because the focus ring and the hover reveal both
    // depend on `:focus-visible`.
    const restored =
      triggerRef.current !== null &&
      attemptFocus(triggerRef.current, { focusVisible: true });

    // suppress the menu's own restore if we successfully restored focus to the trigger
    if (restored) {
      event.preventDefault();
    }
  }, []);

  return { triggerRef, onCloseAutoFocus };
}
